import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Save, X, Shield } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Module × Action permission matrix. Every cell is tickable: a cell either maps to an existing
// backend permission (cols[action]) or gets a generated key `${slug}.${action}`. Existing keys
// are enforced by the app; generated ones are stored on the role and reserved for finer control.
const ACTION_COLS = ["View", "Edit", "Delete", "Approve", "Award", "Manage"];
const MODULE_MATRIX = [
  { module: "Dashboard",       slug: "dashboard",      cols: { View: "dashboard.view" } },
  { module: "RFQ Register",    slug: "rfq",            cols: { View: "rfq.view" } },
  { module: "NPD Costings",    slug: "npd",            cols: { View: "npd.view", Edit: "npd.edit_all", Delete: "npd.delete", Approve: "npd.approve", Award: "npd.award" } },
  { module: "Approved Quotes", slug: "approved",       cols: { View: "view_approved" } },
  { module: "Cost Breakdown",  slug: "cost_breakdown", cols: { View: "view_cost_breakdown", Edit: "npd.edit_all" } },
  { module: "Under-Approval",  slug: "under_approval", cols: { Delete: "under_approval.delete" } },
  { module: "Projects",        slug: "project",        cols: { Delete: "project.delete", Approve: "project.approve", Manage: "customer_price.manage" } },
  { module: "Assemblies",      slug: "assembly",       cols: { View: "assembly.view", Approve: "assembly.approve", Award: "assembly.award" } },
  { module: "APQP",            slug: "apqp",           cols: { View: "apqp.view", Edit: "apqp.edit", Approve: "apqp.approve", Manage: "apqp.manage_master" } },
  { module: "Ballooning",      slug: "ballooning",     cols: { View: "ballooning.view", Edit: "ballooning.edit", Delete: "ballooning.delete", Manage: "ballooning.manage" } },
  { module: "Customers",       slug: "customer",       cols: { Manage: "manage_customers" } },
  { module: "Materials",       slug: "material",       cols: { View: "material.view", Manage: "manage_materials" } },
  { module: "Operations",      slug: "operation",      cols: { View: "operation.view" } },
  { module: "Units (Plants)",  slug: "unit",           cols: { Manage: "manage_units" } },
  { module: "Users",           slug: "user",           cols: { Manage: "manage_users" } },
  { module: "Roles",           slug: "role",           cols: { Manage: "manage_roles" } },
  { module: "API Keys",        slug: "api_key",        cols: { Manage: "manage_api_keys" } },
  { module: "Process Master",  slug: "process_master", cols: { Edit: "edit_process_master" } },
  { module: "App Settings",    slug: "settings",       cols: { Edit: "edit_settings" } },
  { module: "Reports",         slug: "report",         cols: { View: "view_reports" } },
  { module: "RBAC Audit",      slug: "rbac_audit",     cols: { View: "view_rbac_audit" } },
  { module: "Backup & Restore",slug: "backup",         cols: { Manage: "backup_restore" } },
];
// Resolve a cell's permission key — existing mapping, or a generated `${slug}.${action}`.
const cellPerm = (row, action) => row.cols[action] || `${row.slug}.${action.toLowerCase()}`;

export default function Roles() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("manage_roles");
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [masterTasks, setMasterTasks] = useState([]);   // APQP master tasks for the task-picker

  async function load() {
    setBusy(true);
    try {
      const [r, c] = await Promise.all([api.get("/roles"), api.get("/roles/catalog")]);
      setRoles(r.data);
      setCatalog(c.data.permissions || []);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    load();
    api.get("/apqp/master").then((r) => setMasterTasks(r.data || [])).catch(() => {});
  }, []);

  function toggleTaskSeq(seq) {
    setEditing((e) => {
      const cur = e.apqp_task_seqs || [];
      return { ...e, apqp_task_seqs: cur.includes(seq) ? cur.filter((s) => s !== seq) : [...cur, seq] };
    });
  }

  function startNew() {
    setEditing({
      role_id: "",
      name: "",
      description: "",
      permissions: [],
      view_margin: true,
      cross_unit: false,
      apqp_own_tasks_only: false,
      apqp_task_seqs: [],
      bypass_mandatory: false,
      is_builtin: false,
      _isNew: true,
    });
    setError("");
  }
  function startEdit(r) {
    setEditing({ ...r });
    setError("");
  }
  function togglePerm(p) {
    setEditing((e) => ({
      ...e,
      permissions: e.permissions.includes(p)
        ? e.permissions.filter((x) => x !== p)
        : [...e.permissions, p],
    }));
  }

  // Every tickable permission (all matrix cells + any catalog-only "other" perms).
  function allSelectablePerms() {
    const matrix = MODULE_MATRIX.flatMap((row) => ACTION_COLS.map((a) => cellPerm(row, a)));
    const mapped = new Set(MODULE_MATRIX.flatMap((r) => Object.values(r.cols)));
    const extra = (catalog || []).filter((p) => !mapped.has(p));
    return [...new Set([...matrix, ...extra])];
  }
  // Grant every permission at once, or clear them all.
  function selectAllPerms(on) {
    setEditing((e) => ({ ...e, permissions: on ? allSelectablePerms() : [] }));
  }
  // Toggle a group of cells together (a whole column or a whole row): if all are on,
  // turn them off; otherwise turn them all on.
  function toggleGroup(perms) {
    setEditing((e) => {
      const cur = new Set(e.permissions);
      const allOn = perms.every((p) => cur.has(p));
      perms.forEach((p) => (allOn ? cur.delete(p) : cur.add(p)));
      return { ...e, permissions: [...cur] };
    });
  }
  async function save() {
    setBusy(true);
    setError("");
    try {
      if (editing._isNew) {
        await api.post("/roles", {
          role_id: editing.role_id,
          name: editing.name,
          description: editing.description,
          permissions: editing.permissions,
          view_margin: editing.view_margin,
          cross_unit: editing.cross_unit,
          apqp_own_tasks_only: !!editing.apqp_own_tasks_only,
          apqp_task_seqs: editing.apqp_own_tasks_only ? (editing.apqp_task_seqs || []) : [],
          bypass_mandatory: !!editing.bypass_mandatory,
        });
      } else {
        await api.put(`/roles/${editing.role_id}`, {
          name: editing.name,
          description: editing.description,
          permissions: editing.permissions,
          view_margin: editing.view_margin,
          cross_unit: editing.cross_unit,
          apqp_own_tasks_only: !!editing.apqp_own_tasks_only,
          apqp_task_seqs: editing.apqp_own_tasks_only ? (editing.apqp_task_seqs || []) : [],
          bypass_mandatory: !!editing.bypass_mandatory,
        });
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }
  async function del(roleId) {
    if (!window.confirm(`Delete role "${roleId}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/roles/${roleId}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="roles-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield size={22} className="text-orange-600" />
          <h1 className="text-2xl font-bold text-slate-900">Roles & Permissions</h1>
        </div>
        {canManage && (
          <button
            onClick={startNew}
            data-testid="role-new-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded-md font-semibold"
          >
            <Plus size={14} /> New Role
          </button>
        )}
      </div>

      {!editing && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="roles-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Role ID</th>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Permissions</th>
                <th className="text-left px-4 py-2 font-medium">Flags</th>
                <th className="text-left px-4 py-2 font-medium">Type</th>
                <th className="text-right px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {roles.map((r) => (
                <tr key={r.role_id} className="hover:bg-slate-50" data-testid={`role-row-${r.role_id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{r.role_id}</td>
                  <td className="px-4 py-2">
                    {r.name}
                    <div className="text-xs text-slate-500">{r.description}</div>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {(r.permissions || []).length} perms
                  </td>
                  <td className="px-4 py-2 text-xs space-x-1">
                    {r.view_margin && (
                      <span className="inline-block px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">
                        margin
                      </span>
                    )}
                    {r.cross_unit && (
                      <span className="inline-block px-1.5 py-0.5 bg-sky-100 text-sky-800 rounded">
                        cross-unit
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {r.is_builtin ? (
                      <span className="text-slate-500">builtin</span>
                    ) : (
                      <span className="text-orange-600 font-semibold">custom</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {canManage && (
                      <>
                        <button
                          onClick={() => startEdit(r)}
                          className="text-sky-600 hover:text-sky-700 p-1 mr-1"
                          data-testid={`role-edit-${r.role_id}`}
                        >
                          <Edit size={14} />
                        </button>
                        {!r.is_builtin && (
                          <button
                            onClick={() => del(r.role_id)}
                            className="text-slate-400 hover:text-red-600 p-1"
                            data-testid={`role-delete-${r.role_id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {editing && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="role-editor">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-800">
              {editing._isNew ? "New Role" : `Edit Role: ${editing.role_id}`}
            </h2>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700">
              <X size={16} />
            </button>
          </div>
          {error && (
            <div className="mb-3 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded" data-testid="role-error">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Role ID *</label>
              <input
                value={editing.role_id}
                disabled={!editing._isNew}
                onChange={(e) => setEditing({ ...editing, role_id: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm font-mono disabled:bg-slate-50"
                placeholder="e.g. senior_engineer"
                data-testid="role-id-input"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Display Name *</label>
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm"
                data-testid="role-name-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-slate-500 mb-1">Description</label>
              <input
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm"
                data-testid="role-desc-input"
              />
            </div>
          </div>
          <div className="flex gap-4 mb-4">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editing.view_margin}
                onChange={(e) => setEditing({ ...editing, view_margin: e.target.checked })}
                data-testid="role-view-margin"
              />
              Can view margin
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editing.cross_unit}
                onChange={(e) => setEditing({ ...editing, cross_unit: e.target.checked })}
                data-testid="role-cross-unit"
              />
              Cross-unit access
            </label>
            <label className="inline-flex items-center gap-2 text-sm" title="In APQP projects, users with this role only see the tasks whose Responsibility is set to this role.">
              <input
                type="checkbox"
                checked={!!editing.apqp_own_tasks_only}
                onChange={(e) => setEditing({ ...editing, apqp_own_tasks_only: e.target.checked })}
                data-testid="role-apqp-own-tasks"
              />
              APQP: only assigned tasks
            </label>
          </div>

          {/* APQP task picker — pick which tasks (1–58, by phase) this role sees. */}
          {editing.apqp_own_tasks_only && (
            <div className="border border-indigo-200 bg-indigo-50/40 rounded-md p-3 mb-4" data-testid="role-apqp-tasks">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">
                  APQP tasks this role can see <span className="text-slate-400">({(editing.apqp_task_seqs || []).length} selected)</span>
                </div>
                <div className="flex gap-2 text-[10px]">
                  <button type="button" onClick={() => setEditing({ ...editing, apqp_task_seqs: masterTasks.map((t) => t.seq) })} className="text-indigo-600 hover:underline">All</button>
                  <button type="button" onClick={() => setEditing({ ...editing, apqp_task_seqs: [] })} className="text-slate-500 hover:underline">None</button>
                </div>
              </div>
              {masterTasks.length === 0 ? (
                <div className="text-xs text-slate-400 italic">Loading APQP tasks…</div>
              ) : (
                Object.entries(masterTasks.reduce((acc, t) => { (acc[t.phase] = acc[t.phase] || []).push(t); return acc; }, {})).map(([phase, ts]) => (
                  <div key={phase} className="mb-2">
                    <div className="text-[10px] font-semibold text-slate-600 mb-1">{phase}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                      {ts.map((t) => (
                        <label key={t.seq} className="flex items-start gap-1.5 text-[11px] leading-snug cursor-pointer" title={t.task_name}>
                          <input type="checkbox" className="mt-0.5"
                            checked={(editing.apqp_task_seqs || []).includes(t.seq)}
                            onChange={() => toggleTaskSeq(t.seq)} data-testid={`role-apqp-task-${t.seq}`} />
                          <span className="text-slate-400 font-mono">{t.seq}.</span>
                          <span className="break-words">{t.task_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          <div className="border border-slate-200 rounded-md p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Module Permissions
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                <button type="button" onClick={() => selectAllPerms(true)} className="font-semibold text-blue-600 hover:underline" data-testid="role-perm-select-all">Select all</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={() => selectAllPerms(false)} className="text-slate-500 hover:underline" data-testid="role-perm-clear-all">Clear all</button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="role-perm-matrix">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b-2 border-slate-200">
                    <th className="py-2 pr-2 font-semibold">Module</th>
                    {ACTION_COLS.map((a) => {
                      const colPerms = MODULE_MATRIX.map((row) => cellPerm(row, a));
                      const allOn = colPerms.every((p) => editing.permissions.includes(p));
                      return (
                        <th key={a} className="py-2 px-2 text-center font-semibold">
                          <button type="button" onClick={() => toggleGroup(colPerms)}
                            className="inline-flex flex-col items-center gap-0.5 hover:text-blue-600"
                            title={`Toggle ${a} for every module`} data-testid={`role-perm-col-${a}`}>
                            <span>{a}</span>
                            <input type="checkbox" readOnly checked={allOn} tabIndex={-1}
                              className="w-3.5 h-3.5 pointer-events-none accent-blue-600" />
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {MODULE_MATRIX.map((row) => (
                    <tr key={row.module} className="hover:bg-slate-50">
                      <td className="py-2 pr-3 font-medium text-slate-800 whitespace-nowrap">
                        {(() => {
                          const rowPerms = ACTION_COLS.map((a) => cellPerm(row, a));
                          const allOn = rowPerms.every((p) => editing.permissions.includes(p));
                          return (
                            <label className="flex items-center gap-2 cursor-pointer" title={`Toggle all actions for ${row.module}`}>
                              <input type="checkbox" checked={allOn} onChange={() => toggleGroup(rowPerms)}
                                className="w-3.5 h-3.5 cursor-pointer accent-blue-600" data-testid={`role-perm-row-${row.slug}`} />
                              {row.module}
                            </label>
                          );
                        })()}
                      </td>
                      {ACTION_COLS.map((a) => {
                        const perm = cellPerm(row, a);
                        const known = !!row.cols[a];
                        return (
                          <td key={a} className="py-2 px-2 text-center">
                            <input
                              type="checkbox"
                              checked={editing.permissions.includes(perm)}
                              onChange={() => togglePerm(perm)}
                              className="w-4 h-4 cursor-pointer accent-blue-600"
                              data-testid={`role-perm-${perm}`}
                              title={known ? perm : `${perm} (reserved — not yet enforced by the app)`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Any catalog permission not covered by the matrix (future-proofing) */}
            {(() => {
              const mapped = new Set(MODULE_MATRIX.flatMap((r) => Object.values(r.cols)));
              const extra = (catalog || []).filter((p) => !mapped.has(p));
              if (!extra.length) return null;
              return (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5 font-semibold">Other permissions</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {extra.map((p) => (
                      <label key={p} className="inline-flex items-center gap-2 text-xs">
                        <input type="checkbox" className="w-4 h-4 accent-blue-600" checked={editing.permissions.includes(p)} onChange={() => togglePerm(p)} data-testid={`role-perm-${p}`} />
                        <span className="font-mono">{p}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditing(null)}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-md hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={busy || !editing.role_id || !editing.name}
              data-testid="role-save-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
            >
              <Save size={14} /> {busy ? "Saving…" : "Save Role"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
